import { useEffect, useState } from "react";

const PAGE_DEFINITIONS = {
  "forms-detail": { pageId: "page.facility.functional-testing.detail.id", pageTitle: "Inspection", resource: "functional_tests" },
  "functional-testing-detail": { pageId: "page.facility.functional-testing.detail.id", pageTitle: "Inspection", resource: "functional_tests" },
  "pm-plan-detail": { pageId: "page.facility.pm-plan.detail.id", pageTitle: "Preventive Maintenance", resource: "pm_plans" },
  "service-request-detail": { pageId: "page.facility.service-request.detail.id", pageTitle: "Service Request", resource: "service_requests" },
  "workorder-detail": {
    pageId: "page.facility.workorder.detail.id", pageTitle: "Work Order Detail", resource: "workorders",
  },
  "equipment-detail": {
    pageId: "page.facility.equipment.detail.id", pageTitle: "Equipment Detail", resource: "equipment",
  },
  dashboard: {
    pageId: "page.facility.dashboard",
    pageTitle: "Dashboard",
  },
  "utility-summary": {
    pageId: "page.facility.utility.summary",
    pageTitle: "Utility Summary",
  },
  savings: {
    pageId: "page.facility.savings",
    pageTitle: "Savings",
  },
};

const pages = new Map();
const listeners = new Set();
const records = new Map();
let activePage = null;

// Matches the Backend's default FACILITY_AI_PAGE_CONTEXT_MAX_AGE_SECONDS.
export const AI_PAGE_CONTEXT_MAX_AGE_MS = 15 * 60 * 1000;
export const pageContextNotice = (reason) => ({
  loading: "Page data is loading. Wait for the current selection to finish loading.",
  error: "Page data could not be refreshed. Refresh the data before analyzing this page.",
  expired: "Page data has expired. Refresh the data before analyzing this page.",
}[reason] || "");

const emit = () => listeners.forEach((listener) => listener());
const pageKey = (companyId, facilityId, pageId) =>
  `${String(companyId)}:${String(facilityId)}:${pageId}`;

const safeText = (value, limit) =>
  String(value || "")
    .replace(/\0/g, " ")
    .trim()
    .slice(0, limit);
const finiteNumber = (value) => {
  if (
    !["number", "string"].includes(typeof value) ||
    (typeof value === "string" && !value.trim())
  )
    return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const sanitizePoints = (points) =>
  (Array.isArray(points) ? points : [])
    .slice(0, 240)
    .map((point) => {
      const value = finiteNumber(point?.value);
      const numericX =
        typeof point?.x === "number" ? finiteNumber(point.x) : null;
      return {
        label: safeText(point?.label, 120) || null,
        x: numericX !== null ? numericX : safeText(point?.x, 120) || null,
        value,
      };
    })
    .filter(Boolean);

const sanitizeChart = (chart) => {
  const sanitized = {
    chart_id: safeText(chart?.chartId || chart?.chart_id, 120),
    title: safeText(chart?.title, 160),
    aliases: (Array.isArray(chart?.aliases) ? chart.aliases : [])
      .slice(0, 8)
      .map((item) => safeText(item, 120))
      .filter(Boolean),
    chart_type: safeText(chart?.chartType || "chart", 40),
    description: safeText(chart?.description, 500),
    filters: Object.fromEntries(
      Object.entries(chart?.filters || {})
        .slice(0, 16)
        .filter(
          ([, value]) =>
            value == null ||
            ["string", "number", "boolean"].includes(typeof value)
        )
        .map(([key, value]) => [
          safeText(key, 80),
          typeof value === "string" ? safeText(value, 300) : value,
        ])
    ),
    source_operation_ids: (Array.isArray(chart?.sourceOperationIds)
      ? chart.sourceOperationIds
      : []
    )
      .slice(0, 12)
      .map((item) => safeText(item, 120))
      .filter(Boolean),
    series: (Array.isArray(chart?.series) ? chart.series : [])
      .slice(0, 12)
      .map((series) => ({
        name: safeText(series?.name, 120),
        unit: safeText(series?.unit, 40) || null,
        role: safeText(series?.role, 80) || null,
        visible: series?.visible !== false,
        points: sanitizePoints(series?.points),
      }))
      .filter((series) => series.name),
    metrics: (Array.isArray(chart?.metrics) ? chart.metrics : [])
      .slice(0, 16)
      .map((metric) => {
        const value = finiteNumber(metric?.value);
        if (!safeText(metric?.name, 120)) return null;
        return {
          name: safeText(metric.name, 120),
          value,
          unit: safeText(metric?.unit, 40) || null,
        };
      })
      .filter(Boolean),
    completeness: ["complete", "partial", "unknown"].includes(
      chart?.completeness
    )
      ? chart.completeness
      : "unknown",
    limitations: (Array.isArray(chart?.limitations) ? chart.limitations : [])
      .slice(0, 12)
      .map((item) => safeText(item, 300))
      .filter(Boolean),
  };
  const missing =
    sanitized.series.some((series) =>
      series.points.some((point) => point.value === null)
    ) || sanitized.metrics.some((metric) => metric.value === null);
  const sourceSeries = Array.isArray(chart?.series) ? chart.series : [];
  const limited =
    sourceSeries.length > 12 ||
    sourceSeries.some((series) => (series.points || []).length > 240) ||
    (Array.isArray(chart?.metrics) && chart.metrics.length > 16);
  if (missing || limited) {
    sanitized.completeness = "partial";
    const warnings = [
      ...(missing ? ["Missing values are not measured zero."] : []),
      ...(limited ? ["The chart snapshot omits some source values."] : []),
    ];
    sanitized.limitations = [
      ...new Set([...warnings, ...sanitized.limitations]),
    ].slice(0, 12);
  }
  return sanitized;
};

const routeDescriptor = (pathname) => {
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  if (parts[0] !== "facility" || parts.length < 4) return null;
  const [, companyId, facilityId, leaf] = parts;
  if (leaf === "assistant") return { assistant: true };
  const definition = PAGE_DEFINITIONS[leaf];
  if (!definition) return null;
  const recordId = definition.resource ? Number(parts[4]) : null;
  if (definition.resource && (parts.length !== 5 || !Number.isSafeInteger(recordId) || recordId <= 0)) return null;
  return {
    ...definition,
    recordId,
    companyId: String(companyId),
    facilityId: String(facilityId),
    route: `/${parts.join("/")}`,
  };
};

export const setActiveAiPageRoute = (pathname) => {
  const descriptor = routeDescriptor(pathname);
  // The Assistant is not itself a source page. Clear the previous page when
  // navigating here so a stale dashboard/chart snapshot cannot be submitted
  // as current-page context and rejected by the Backend with HTTP 400.
  if (descriptor?.assistant) {
    activePage = null;
    emit();
    return;
  }
  activePage = descriptor;
  emit();
};

export const registerAiChartContext = ({
  companyId,
  facilityId,
  pageId,
  chart,
  status = "ready",
  capturedAt = new Date().toISOString(),
  refresh,
}) => {
  if (!companyId || !facilityId || !pageId || !chart) return;
  const sanitized = sanitizeChart(chart);
  if (!sanitized.chart_id || !sanitized.title) return;
  const key = pageKey(companyId, facilityId, pageId);
  const existing = pages.get(key) || { charts: new Map() };
  existing.charts.set(sanitized.chart_id, { chart: sanitized, status, capturedAt, refresh });
  pages.set(key, existing);
  emit();
};

export const unregisterAiChartContext = ({
  companyId,
  facilityId,
  pageId,
  chartId,
}) => {
  if (!companyId || !facilityId || !pageId || !chartId) return;
  const key = pageKey(companyId, facilityId, pageId);
  const existing = pages.get(key);
  if (!existing) return;
  existing.charts.delete(String(chartId));
  if (!existing.charts.size) {
    pages.delete(key);
  } else {
    pages.set(key, existing);
  }
  emit();
};

export const registerAiRecordContext = ({ companyId, facilityId, resource, recordId }) => {
  const key = `${companyId}:${facilityId}:${resource}:${recordId}`;
  const token = {};
  const registrations = records.get(key) || new Set();
  registrations.add(token);
  records.set(key, registrations);
  emit();
  return () => {
    registrations.delete(token);
    if (!registrations.size) records.delete(key);
    emit();
  };
};

export const getCurrentAiPageContextStatus = (companyId, facilityId) => {
  if (
    !activePage ||
    String(companyId) !== activePage.companyId ||
    String(facilityId) !== activePage.facilityId
  ) {
    return { context: null, reason: "unavailable" };
  }
  if (activePage.resource) {
    const key = `${companyId}:${facilityId}:${activePage.resource}:${activePage.recordId}`;
    if (!records.has(key)) return { context: null, reason: "unavailable" };
    return { reason: null, context: {
      version: 1, page_id: activePage.pageId, page_title: activePage.pageTitle,
      route: activePage.route, captured_at: new Date().toISOString(),
      company_id: Number(companyId), facility_id: Number(facilityId), charts: [],
      record: { resource: activePage.resource, record_id: activePage.recordId },
    } };
  }
  const stored = pages.get(pageKey(companyId, facilityId, activePage.pageId));
  const entries = stored ? Array.from(stored.charts.values()) : [];
  if (!entries.length) return { context: null, reason: "unavailable" };
  if (entries.some((entry) => entry.status === "error")) return { context: null, reason: "error" };
  if (entries.some((entry) => entry.status !== "ready")) return { context: null, reason: "loading" };
  const capturedAt = Math.min(...entries.map((entry) => Date.parse(entry.capturedAt)));
  const expiresAt = capturedAt + AI_PAGE_CONTEXT_MAX_AGE_MS;
  if (!Number.isFinite(capturedAt) || Date.now() >= expiresAt) return { context: null, reason: "expired" };
  return { reason: null, expiresAt, context: {
    version: 1,
    page_id: activePage.pageId,
    page_title: activePage.pageTitle,
    route: activePage.route,
    captured_at: new Date(capturedAt).toISOString(),
    company_id: Number(companyId),
    facility_id: Number(facilityId),
    charts: entries.slice(0, 12).map((entry) => entry.chart),
  } };
};

export const getCurrentAiPageContext = (companyId, facilityId) =>
  getCurrentAiPageContextStatus(companyId, facilityId).context;

export const requireCurrentAiPageContext = (companyId, facilityId) => {
  const state = getCurrentAiPageContextStatus(companyId, facilityId);
  if (!state.context) throw new Error(pageContextNotice(state.reason) || "The current page is no longer available. Reopen the source page or turn off Current page.");
  return state.context;
};

export const refreshCurrentAiPageContext = async (companyId, facilityId) => {
  if (!activePage || String(companyId) !== activePage.companyId || String(facilityId) !== activePage.facilityId) return;
  const stored = pages.get(pageKey(companyId, facilityId, activePage.pageId));
  const entries = stored ? Array.from(stored.charts.values()) : [];
  // Pages without a source refresh callback must reload the actual data;
  // changing the capture timestamp alone would disguise an expired snapshot.
  if (!entries.length || entries.some((entry) => typeof entry.refresh !== "function")) {
    window.location.reload();
    return;
  }
  await Promise.allSettled([...new Set(entries.map((entry) => entry.refresh))].map((refresh) => Promise.resolve().then(refresh)));
};

export const subscribeAiPageContext = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useCurrentAiPageContextStatus = (companyId, facilityId) => {
  const [snapshot, setSnapshot] = useState(() =>
    getCurrentAiPageContextStatus(companyId, facilityId)
  );
  useEffect(() => {
    let timer;
    const update = () => {
      clearTimeout(timer);
      const next = getCurrentAiPageContextStatus(companyId, facilityId);
      setSnapshot(next);
      if (next.expiresAt) timer = setTimeout(update, Math.max(1, next.expiresAt - Date.now() + 1));
    };
    update();
    const unsubscribe = subscribeAiPageContext(update);
    window.addEventListener("focus", update);
    document.addEventListener("visibilitychange", update);
    return () => {
      clearTimeout(timer);
      unsubscribe();
      window.removeEventListener("focus", update);
      document.removeEventListener("visibilitychange", update);
    };
  }, [companyId, facilityId]);
  return snapshot;
};

export const useCurrentAiPageContext = (companyId, facilityId) =>
  useCurrentAiPageContextStatus(companyId, facilityId).context;

export const resetAiPageContextStore = () => {
  pages.clear();
  records.clear();
  activePage = null;
  emit();
};
